import React, { useState, useEffect, useCallback, useContext } from 'react';

import { Button, Container, Table, Spinner, Modal, Form, InputGroup, Row, Col, Alert, Collapse, OverlayTrigger, Tooltip, Dropdown, ButtonGroup, Badge } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { getProducts, createProduct, updateProduct, deleteProduct, getSuppliers, uploadProductImage } from '../services/api';
import { trackAction } from '../services/analytics';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import Pagination from '../components/Pagination';
import { useDebounce } from '../hooks/useDebounce';
import usePermissions, { PERMISSIONS } from '../hooks/usePermissions';
import AuthContext from '../context/AuthContext';
import { FaEdit, FaTrash, FaTable, FaTh } from 'react-icons/fa';
import { Sparkles, Wand2 } from 'lucide-react';
import CreateExpenseModal from '../components/CreateExpenseModal';
import ExcelUploadModal from '../components/ExcelUploadModal';
import ProductSuggestionsModal from '../components/ProductSuggestionsModal';
import GenerateProductsModal from '../components/GenerateProductsModal';
import CurrencyInput from '../components/CurrencyInput';
import NumberInput from '../components/NumberInput';
import PageSizeSelector from '../components/PageSizeSelector';
import useIsMobile from '../hooks/useIsMobile';

const EditableProductCell = ({ value, row, field, type, onSave, formatDisplay, isWarning, warningMessage }) => {
    const [localValue, setLocalValue] = useState(value);

    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    const handleBlur = () => {
        onSave(row, field, localValue);
    };

    return (
        <>
            <span className={`d-md-none ${isWarning ? 'text-danger fw-bold' : ''}`} title={isWarning ? warningMessage : ''}>
                {formatDisplay ? formatDisplay(value) : (value || 0)}
                {isWarning && ' ⚠️'}
            </span>
            <div className="d-none d-md-block">
                {isWarning ? (
                    <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{warningMessage}</Tooltip>}
                    >
                        <div>
                            {type === 'currency' ? (
                                <CurrencyInput
                                    value={localValue}
                                    onChange={setLocalValue}
                                    onBlur={handleBlur}
                                    placeholder="0"
                                    className="border-danger text-danger"
                                />
                            ) : (
                                <NumberInput
                                    value={localValue}
                                    onChange={setLocalValue}
                                    onBlur={handleBlur}
                                    placeholder="0"
                                    className="border-danger text-danger"
                                />
                            )}
                        </div>
                    </OverlayTrigger>
                ) : (
                    type === 'currency' ? (
                        <CurrencyInput
                            value={localValue}
                            onChange={setLocalValue}
                            onBlur={handleBlur}
                            placeholder="0"
                        />
                    ) : (
                        <NumberInput
                            value={localValue}
                            onChange={setLocalValue}
                            onBlur={handleBlur}
                            placeholder="0"
                        />
                    )
                )}
            </div>
        </>
    );
};

const ProductsPage = () => {
    const { user, business } = useContext(AuthContext);
    const { hasPermission } = usePermissions(user);
    const { t, i18n } = useTranslation();
    const isMobile = useIsMobile();
    const [products, setProducts] = useState([]);

    const isSubscriptionActive = ['active', 'trialing'].includes(business?.subscription_status);
    const aiSuggestionsEnabled = !!business?.plan_features?.ai_product_suggestions;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [modalData, setModalData] = useState({ name: '', unit_price: '', cost_price: '', description: '', available_quantity: '', minimum_stock: '', catalog_visible: false, image_url: '', tags: [] });
    const [editingProduct, setEditingProduct] = useState(null);
    const [imageUploading, setImageUploading] = useState(false);
    const [imageUploadDebugError, setImageUploadDebugError] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const location = useLocation();

    // Initialize sort from URL to avoid extra fetch
    const getInitialSort = () => {
        const query = new URLSearchParams(location.search);
        return query.get('sort') || '';
    };
    const [sort, setSort] = useState(getInitialSort);
    const [showFilters, setShowFilters] = useState(false);
    const [viewMode, setViewMode] = useState(() => localStorage.getItem('productsViewMode') || 'table');

    const toggleViewMode = (mode) => {
        setViewMode(mode);
        localStorage.setItem('productsViewMode', mode);
    };

    // Expense Modal States
    const [suppliers, setSuppliers] = useState([]);
    const [showExpenseModal, setShowExpenseModal] = useState(false);
    const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
    const [showAiSuggestionsModal, setShowAiSuggestionsModal] = useState(false);
    const [showGenerateProductsModal, setShowGenerateProductsModal] = useState(false);

    const formatCurrency = useCallback((num) => {
        return '$' + parseFloat(num || 0).toLocaleString(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 2, style: 'decimal' });
    }, [i18n.language]);

    const calculateProfit = useCallback((price, cost) => {
        const unitPrice = parseFloat(price || 0);
        const costPrice = parseFloat(cost || 0);
        const profit = unitPrice - costPrice;
        let margin = 0;
        if (unitPrice > 0) {
            margin = (profit / unitPrice) * 100;
        }
        return {
            profit,
            margin
        };
    }, []);

    const fetchProducts = useCallback(async () => {
        try {
            setLoading(true);
            const response = await getProducts({ page: currentPage, limit: pageSize, name: debouncedSearchTerm, sort });
            setProducts(response.data.products);
            setTotalPages(response.data.totalPages);
        } catch (error) {
            toast.error(t('productsPage.toasts.fetchFailed'));
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, debouncedSearchTerm, sort, t]);

    useEffect(() => {
        const loadData = async () => {
            if (hasPermission(PERMISSIONS.VIEW_PRODUCTS)) {
                await fetchProducts();
                // Load suppliers for expense modal
                try {
                    const suppliersRes = await getSuppliers();
                    setSuppliers(suppliersRes.data.suppliers || []);
                } catch (error) {
                    console.error('Error loading suppliers:', error);
                }
            } else {
                setLoading(false);
                setError(t('productsPage.toasts.permissionError'));
            }
        };
        loadData();
    }, [fetchProducts, hasPermission, t]);

    const handleModalClose = () => {
        setShowModal(false);
        setEditingProduct(null);
        setModalData({ name: '', unit_price: '', cost_price: '', description: '', available_quantity: '', minimum_stock: '', catalog_visible: false, image_url: '', tags: [] });
    };

    const handleCreateClick = () => {
        setEditingProduct(null);
        setModalData({ name: '', unit_price: '', cost_price: '', description: '', available_quantity: '', minimum_stock: '', catalog_visible: false, image_url: '', tags: [] });
        setShowModal(true);
    };

    const handleProductImageUpload = async (file) => {
        setImageUploading(true);
        setImageUploadDebugError(null);
        try {
            const res = await uploadProductImage(file);
            setModalData(prev => ({ ...prev, image_url: res.data.image_url }));
            toast.success(t('productsPage.modal.imageUploaded'));
        } catch (err) {
            toast.error(t('productsPage.modal.imageUploadFailed'));
            if (process.env.NODE_ENV === 'development') {
                setImageUploadDebugError({
                    message: err.message,
                    status: err.response?.status ?? null,
                    statusText: err.response?.statusText ?? null,
                    data: err.response?.data ?? null,
                    url: err.config?.url ?? null,
                    baseURL: err.config?.baseURL ?? null,
                    fileType: file.type,
                    fileName: file.name,
                    fileSize: file.size,
                });
            }
        } finally {
            setImageUploading(false);
        }
    };

    const handleEditClick = (product) => {
        setEditingProduct(product);
        setModalData(product);
        setShowModal(true);
    };

    const handleDelete = async (productId) => {
        const result = await Swal.fire({
            title: t('areYouSure'),
            text: t('productsPage.toasts.deleteConfirm'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: 'var(--ev-action-danger-bg)',
            cancelButtonColor: 'var(--ev-bg-subtle)',
            confirmButtonText: t('yesDelete'),
            cancelButtonText: t('cancel')
        });

        if (result.isConfirmed) {
            const toastId = toast.loading(t('productsPage.toasts.deleting'));
            try {
                await deleteProduct(productId);
                fetchProducts(); // Refetch products
                trackAction('product_deleted');
                toast.success(t('productsPage.toasts.deleted'), { id: toastId });
            } catch (error) {
                toast.error(t('productsPage.toasts.deleteFailed'), { id: toastId });
            }
        }
    };

    const handleModalSubmit = async (e) => {
        e.preventDefault();
        const toastId = toast.loading(editingProduct ? t('productsPage.toasts.updating') : t('productsPage.toasts.creating'));
        try {
            if (editingProduct) {
                await updateProduct(editingProduct.product_id, modalData);
                trackAction('product_updated');
                toast.success(t('productsPage.toasts.updated'), { id: toastId });
            } else {
                await createProduct(modalData);
                trackAction('product_created');
                toast.success(t('productsPage.toasts.created'), { id: toastId });
            }
            fetchProducts(); // Refetch products
            handleModalClose();
        } catch (error) {
            trackAction('product_save_failed', { is_edit: !!editingProduct });
            toast.error(editingProduct ? t('productsPage.toasts.updateFailed') : t('productsPage.toasts.createFailed'), { id: toastId });
        }
    };

    const handleModalChange = (e) => {
        setModalData({ ...modalData, [e.target.name]: e.target.value });
    };

    const handleInlineSave = async (product, field, value) => {
        if (product[field] === value) return;

        try {
            await updateProduct(product.product_id, { ...product, [field]: value });
            trackAction('product_updated', { inline: true }); // edición inline en la tabla
            toast.success(t('productsPage.toasts.updated'));
            fetchProducts();
        } catch (error) {
            trackAction('product_save_failed', { is_edit: true, inline: true });
            toast.error(t('productsPage.toasts.updateFailed'));
        }
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (e) => {
        setPageSize(parseInt(e.target.value, 10));
        setCurrentPage(1);
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleCreateExpenseClick = () => {
        setShowExpenseModal(true);
    };

    const canEdit = hasPermission(PERMISSIONS.EDIT_PRODUCTS);
    const canCreate = hasPermission(PERMISSIONS.CREATE_PRODUCTS);
    const canDelete = hasPermission(PERMISSIONS.DELETE_PRODUCTS);

    return (
        <Container className="mt-4">
            <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                    <h2 className="mb-0">{t('productsPage.title')}</h2>
                </div>
                {canCreate && (
                    <div className="d-none d-md-flex gap-2">
                        <div title={!isSubscriptionActive ? t('subscriptionRequired') : ''}>
                            <Dropdown as={ButtonGroup}>
                                <Button
                                    variant="primary"
                                    onClick={handleCreateClick}
                                   
                                    disabled={!isSubscriptionActive}
                                >
                                    {t('productsPage.newProductButton')}
                                </Button>

                                <Dropdown.Toggle split variant="primary" disabled={!isSubscriptionActive} id="dropdown-split-basic" />

                                <Dropdown.Menu align="end">
                                    <Dropdown.Item onClick={() => setShowBulkUploadModal(true)}>
                                        <i className="bi bi-file-earmark-excel me-2"></i>
                                        {t('productsPage.bulkUpload.dropdownOption')}
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        onClick={() => aiSuggestionsEnabled && setShowAiSuggestionsModal(true)}
                                        disabled={!aiSuggestionsEnabled}
                                        title={!aiSuggestionsEnabled ? t('common.featureNotAvailable') : ''}
                                    >
                                        <Sparkles size={14} className="me-2" />
                                        {t('productsPage.aiSuggestions.dropdownOption', 'Sugerir productos con IA')}
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        onClick={() => aiSuggestionsEnabled && setShowGenerateProductsModal(true)}
                                        disabled={!aiSuggestionsEnabled}
                                        title={!aiSuggestionsEnabled ? t('common.featureNotAvailable') : ''}
                                    >
                                        <Wand2 size={14} className="me-2" />
                                        {t('productsPage.aiGenerate.dropdownOption', 'Generar productos con IA')}
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                        <div title={!isSubscriptionActive ? t('subscriptionRequired') : ''}>
                            <Button
                                variant="danger"
                                onClick={handleCreateExpenseClick}
                                disabled={!isSubscriptionActive}
                            >
                                {t('expensesPage.newExpenseButton', 'Registrar Gasto')}
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Mobile Fixed Bottom Buttons */}
            {canCreate && (
                <div className="d-md-none fixed-bottom p-3 bg-white border-top" style={{ zIndex: 1000 }}>
                    <div className="d-flex gap-2">
                        <Dropdown as={ButtonGroup} className="flex-grow-1" drop="up">
                            <Button
                                variant="primary"
                                onClick={handleCreateClick}
                                className="flex-grow-1"
                               
                                disabled={!isSubscriptionActive}
                            >
                                {t('productsPage.newProductButton')}
                            </Button>
                            <Dropdown.Toggle split variant="primary" disabled={!isSubscriptionActive} />
                            <Dropdown.Menu align="end">
                                <Dropdown.Item onClick={() => setShowBulkUploadModal(true)}>
                                    <i className="bi bi-file-earmark-excel me-2"></i>
                                    {t('productsPage.bulkUpload.dropdownOption')}
                                </Dropdown.Item>
                                <Dropdown.Item
                                    onClick={() => aiSuggestionsEnabled && setShowAiSuggestionsModal(true)}
                                    disabled={!aiSuggestionsEnabled}
                                    title={!aiSuggestionsEnabled ? t('common.featureNotAvailable') : ''}
                                >
                                    <Sparkles size={14} className="me-2" />
                                    {t('productsPage.aiSuggestions.dropdownOption', 'Sugerir productos con IA')}
                                </Dropdown.Item>
                                <Dropdown.Item
                                    onClick={() => aiSuggestionsEnabled && setShowGenerateProductsModal(true)}
                                    disabled={!aiSuggestionsEnabled}
                                    title={!aiSuggestionsEnabled ? t('common.featureNotAvailable') : ''}
                                >
                                    <Wand2 size={14} className="me-2" />
                                    {t('productsPage.aiGenerate.dropdownOption', 'Generar productos con IA')}
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>

                        <Button
                            variant="danger"
                            onClick={handleCreateExpenseClick}
                            disabled={!isSubscriptionActive}
                            className="flex-grow-1"
                        >
                            {t('expensesPage.newExpenseButton', 'Registrar Gasto')}
                        </Button>
                    </div>
                </div>
            )}
            <p>{t('productsPage.subtitle')}</p>

            <div className="d-md-none mb-3">
                <Button variant="link" onClick={() => setShowFilters(!showFilters)} className="p-0 text-decoration-none">
                    {showFilters ? t('productsPage.hideFilters', 'Ocultar filtros') : t('productsPage.viewFilters', 'Ver filtros')}
                </Button>
            </div>

            <Collapse in={showFilters || window.innerWidth >= 768}>
                <div id="products-filters">
                    <InputGroup className="mb-3">
                        <Form.Control
                            placeholder={t('productsPage.searchPlaceholder')}
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                    </InputGroup>
                </div>
            </Collapse>

            {sort === 'low_rotation' && (
                <Alert variant="info" className="mb-3">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <strong>🔍 {t('productsPage.lowRotationFilter', 'Mostrando productos de baja rotación')}</strong>
                            <p className="mb-0 small">{t('productsPage.lowRotationDescription', 'Productos ordenados por menor cantidad de ventas en los últimos 30 días.')}</p>
                        </div>
                        <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => {
                                setSort('');
                                window.history.pushState({}, '', '/products');
                            }}
                        >
                            {t('common.clearFilter', 'Limpiar filtro')}
                        </Button>
                    </div>
                </Alert>
            )}

            {error && <Alert variant="danger">{error}</Alert>}

            {/* View Mode Toggle */}
            <div className="d-flex justify-content-end align-items-center mb-3">
                <div
                    className="sales-view-toggle"
                    role="group"
                    aria-label={t('productsPage.viewToggle.label', 'Cambiar vista')}
                >
                    <button
                        id="products-view-table"
                        className={`sales-view-toggle__btn${viewMode === 'table' ? ' sales-view-toggle__btn--active' : ''}`}
                        onClick={() => toggleViewMode('table')}
                        aria-pressed={viewMode === 'table'}
                        title={t('productsPage.viewToggle.tableView', 'Vista tabla')}
                        aria-label={t('productsPage.viewToggle.tableView', 'Vista tabla')}
                    >
                        <FaTable aria-hidden="true" />
                        <span className="sales-view-toggle__label">{t('productsPage.viewToggle.tableView', 'Tabla')}</span>
                    </button>
                    <button
                        id="products-view-cards"
                        className={`sales-view-toggle__btn${viewMode === 'cards' ? ' sales-view-toggle__btn--active' : ''}`}
                        onClick={() => toggleViewMode('cards')}
                        aria-pressed={viewMode === 'cards'}
                        title={t('productsPage.viewToggle.cardsView', 'Vista tarjetas')}
                        aria-label={t('productsPage.viewToggle.cardsView', 'Vista tarjetas')}
                    >
                        <FaTh aria-hidden="true" />
                        <span className="sales-view-toggle__label">{t('productsPage.viewToggle.cardsView', 'Tarjetas')}</span>
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="text-center"><Spinner animation="border" /></div>
            ) : viewMode === 'table' ? (
                <div className="modern-table-wrapper mt-1">
                    <Table hover responsive className="modern-table">
                        <thead>
                            <tr>
                                <th>{t('productsPage.table.name')}</th>
                                <th>{t('productsPage.table.unitPrice')}</th>
                                <th>{t('productsPage.table.purchasePrice')}</th>
                                <th>{t('productsPage.table.profit')}</th>
                                <th>{t('productsPage.table.availableQuantity')}</th>
                                <th>{t('productsPage.table.actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {products.length > 0 ? products.map(product => (
                                <tr key={product.product_id}>
                                    <td>{product.name}</td>
                                    <td>
                                        <EditableProductCell
                                            value={product.unit_price}
                                            row={product}
                                            field="unit_price"
                                            type="currency"
                                            onSave={handleInlineSave}
                                            formatDisplay={formatCurrency}
                                        />
                                    </td>
                                    <td>
                                        <EditableProductCell
                                            value={product.cost_price}
                                            row={product}
                                            field="cost_price"
                                            type="currency"
                                            onSave={handleInlineSave}
                                            formatDisplay={formatCurrency}
                                        />
                                    </td>
                                    <td>
                                        {(() => {
                                            const { profit, margin } = calculateProfit(product.unit_price, product.cost_price);
                                            return (
                                                <span>
                                                    {formatCurrency(profit)} <small className="text-muted">({margin.toFixed(0)}%)</small>
                                                </span>
                                            );
                                        })()}
                                    </td>
                                    <td>
                                        <EditableProductCell
                                            value={product.available_quantity}
                                            row={product}
                                            field="available_quantity"
                                            type="number"
                                            onSave={handleInlineSave}
                                            isWarning={parseFloat(product.minimum_stock) > 0 && parseFloat(product.available_quantity || 0) < parseFloat(product.minimum_stock)}
                                            warningMessage={t('productsPage.lowStockWarning', 'Stock bajo: menor al mínimo requerido')}
                                        />
                                    </td>
                                    <td className="text-center">
                                        <div className="btn-actions">
                                        {product.catalog_visible && (
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={<Tooltip>{t('productsPage.table.inCatalog')}</Tooltip>}
                                            >
                                                <span style={{ fontSize: '1.1rem', cursor: 'default' }}>🛒</span>
                                            </OverlayTrigger>
                                        )}
                                        {canEdit && (
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={() => handleEditClick(product)}
                                                title={t('productsPage.editButton')}
                                                aria-label={t('productsPage.editButton')}
                                            >
                                                <FaEdit />
                                            </Button>
                                        )}
                                        {canDelete && (
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => handleDelete(product.product_id)}
                                                title={t('productsPage.deleteButton')}
                                                aria-label={t('productsPage.deleteButton')}
                                            >
                                                <FaTrash />
                                            </Button>
                                        )}
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan="6" className="text-center py-4 text-muted">{t('productsPage.noProducts')}</td></tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            ) : (
                /* ── Cards view ── */
                <div>
                    {products.length === 0 ? (
                        <div className="text-center py-5 text-muted">{t('productsPage.noProducts')}</div>
                    ) : (
                        <div className="sales-cards-grid">
                            {products.map(product => {
                                const { profit, margin } = calculateProfit(product.unit_price, product.cost_price);
                                const isLowStock = parseFloat(product.minimum_stock) > 0 && parseFloat(product.available_quantity || 0) < parseFloat(product.minimum_stock);
                                return (
                                    <div
                                        key={product.product_id}
                                        className={`product-card${isLowStock ? ' product-card--low-stock' : ''}`}
                                    >
                                        <div className="product-card__image-wrap">
                                            {product.image_url ? (
                                                <img
                                                    src={product.image_url}
                                                    alt={product.name}
                                                    className="product-card__image"
                                                    onClick={() => setPreviewImage({ url: product.image_url, name: product.name })}
                                                    role="button"
                                                    tabIndex={0}
                                                    title={t('productsPage.expandImage', 'Ver imagen completa')}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            setPreviewImage({ url: product.image_url, name: product.name });
                                                        }
                                                    }}
                                                />
                                            ) : (
                                                <div className="product-card__image-placeholder">
                                                    <img
                                                        src="/logo_evazard_lineal.png"
                                                        alt={product.name}
                                                        className="product-card__image-placeholder-logo"
                                                    />
                                                </div>
                                            )}
                                            {product.catalog_visible && (
                                                <span className="product-card__catalog-badge" title={t('productsPage.table.inCatalog')}>🛒</span>
                                            )}
                                        </div>
                                        <div className="product-card__body">
                                            <div className="product-card__name" title={product.name}>{product.name}</div>
                                            <div className="product-card__prices">
                                                <div className="product-card__price-item">
                                                    <span className="product-card__price-label">{t('productsPage.table.unitPrice')}</span>
                                                    <span className="product-card__price-value product-card__price-value--sale">{formatCurrency(product.unit_price)}</span>
                                                </div>
                                                <div className="product-card__price-item">
                                                    <span className="product-card__price-label">{t('productsPage.table.purchasePrice')}</span>
                                                    <span className="product-card__price-value">{formatCurrency(product.cost_price)}</span>
                                                </div>
                                                <div className="product-card__price-item">
                                                    <span className="product-card__price-label">{t('productsPage.table.profit')}</span>
                                                    <span className="product-card__price-value product-card__price-value--profit">
                                                        {formatCurrency(profit)} <small>({margin.toFixed(0)}%)</small>
                                                    </span>
                                                </div>
                                            </div>
                                            <div
                                                className={`product-card__stock${isLowStock ? ' product-card__stock--warning' : ''}`}
                                                title={isLowStock ? t('productsPage.lowStockWarning', 'Stock bajo: menor al mínimo requerido') : ''}
                                            >
                                                <span className="product-card__stock-label">{t('productsPage.table.availableQuantity')}</span>
                                                <span className="product-card__stock-value">{product.available_quantity ?? 0}{isLowStock && ' ⚠️'}</span>
                                            </div>
                                        </div>
                                        <div className="product-card__actions btn-actions">
                                            {canEdit && (
                                                <Button
                                                    variant="secondary"
                                                    size="sm"
                                                    onClick={() => handleEditClick(product)}
                                                    title={t('productsPage.editButton')}
                                                    aria-label={t('productsPage.editButton')}
                                                >
                                                    <FaEdit />
                                                </Button>
                                            )}
                                            {canDelete && (
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => handleDelete(product.product_id)}
                                                    title={t('productsPage.deleteButton')}
                                                    aria-label={t('productsPage.deleteButton')}
                                                >
                                                    <FaTrash />
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            <div className="d-flex justify-content-between align-items-center mt-3">
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
                <PageSizeSelector pageSize={pageSize} onPageSizeChange={handlePageSizeChange} />
            </div>

            <Modal show={showModal} onHide={handleModalClose} backdrop={isMobile ? 'static' : true}>
                <Modal.Header closeButton>
                    <Modal.Title>{editingProduct ? t('productsPage.modal.editTitle') : t('productsPage.modal.createTitle')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleModalSubmit}>
                    <fieldset disabled={editingProduct && !canEdit}>
                        <Modal.Body>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('productsPage.modal.nameLabel')}</Form.Label>
                                <Form.Control type="text" name="name" value={modalData.name} onChange={handleModalChange} required />
                            </Form.Group>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('productsPage.modal.priceLabel')}</Form.Label>
                                        <CurrencyInput
                                            value={modalData.unit_price}
                                            onChange={(value) => setModalData({ ...modalData, unit_price: value })}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('productsPage.modal.costLabel')}</Form.Label>
                                        <CurrencyInput
                                            value={modalData.cost_price}
                                            onChange={(value) => setModalData({ ...modalData, cost_price: value })}
                                            placeholder="0"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('productsPage.modal.availableQuantityLabel')}</Form.Label>
                                        <NumberInput
                                            value={modalData.available_quantity}
                                            onChange={(value) => setModalData({ ...modalData, available_quantity: value })}
                                            placeholder="0"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>{t('productsPage.modal.minimumStockLabel')}</Form.Label>
                                        <NumberInput
                                            value={modalData.minimum_stock}
                                            onChange={(value) => setModalData({ ...modalData, minimum_stock: value })}
                                            placeholder="0"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Form.Text className="text-muted d-block mb-3">
                                {t('productsPage.modal.inventoryHint')}
                            </Form.Text>
                            <Form.Group>
                                <Form.Label>{t('productsPage.modal.descriptionLabel')}</Form.Label>
                                <Form.Control as="textarea" rows={3} name="description" value={modalData.description} onChange={handleModalChange} />
                            </Form.Group>
                            <hr />
                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="switch"
                                    id="catalog-visible-switch"
                                    label={t('productsPage.modal.catalogVisibleLabel')}
                                    checked={!!modalData.catalog_visible}
                                    onChange={(e) => setModalData(prev => ({ ...prev, catalog_visible: e.target.checked }))}
                                />
                                <Form.Text className="text-muted">
                                    {t('productsPage.modal.catalogVisibleHint')}
                                </Form.Text>
                            </Form.Group>
                            {modalData.catalog_visible && (
                                <>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('productsPage.modal.tagsLabel')}</Form.Label>
                                    <div className="d-flex flex-wrap gap-1 mb-2">
                                        {(modalData.tags || []).map(tag => (
                                            <Badge
                                                key={tag}
                                                bg="secondary"
                                                style={{ cursor: 'pointer', borderRadius: 20, fontSize: '0.8rem', padding: '5px 10px' }}
                                                onClick={() => setModalData(prev => ({
                                                    ...prev,
                                                    tags: prev.tags.filter(t => t !== tag)
                                                }))}
                                            >
                                                {tag} ×
                                            </Badge>
                                        ))}
                                    </div>
                                    <Form.Control
                                        type="text"
                                        placeholder={t('productsPage.modal.tagsPlaceholder')}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ',') {
                                                e.preventDefault();
                                                const val = e.target.value.trim().toLowerCase();
                                                if (val && !(modalData.tags || []).includes(val)) {
                                                    setModalData(prev => ({ ...prev, tags: [...(prev.tags || []), val] }));
                                                }
                                                e.target.value = '';
                                            }
                                        }}
                                    />
                                    <Form.Text className="text-muted">{t('productsPage.modal.tagsHint')}</Form.Text>
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('productsPage.modal.imageLabel')}</Form.Label>
                                    {modalData.image_url && (
                                        <div className="mb-2">
                                            <img
                                                src={modalData.image_url}
                                                alt="product"
                                                style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8 }}
                                            />
                                        </div>
                                    )}
                                    <div className="d-flex align-items-center gap-2">
                                        <Form.Control
                                            type="file"
                                            accept="image/jpeg,image/jpg,image/png"
                                            disabled={imageUploading}
                                            onChange={(e) => {
                                                if (e.target.files[0]) handleProductImageUpload(e.target.files[0]);
                                            }}
                                        />
                                        {imageUploading && <Spinner size="sm" animation="border" />}
                                    </div>
                                </Form.Group>
                                </>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleModalClose}>{t('productsPage.modal.closeButton')}</Button>
                            {(canCreate || (editingProduct && canEdit)) && (
                                <Button variant="primary" type="submit">{t('productsPage.modal.saveButton')}</Button>
                            )}
                        </Modal.Footer>
                    </fieldset>
                </Form>
            </Modal>

            {/* Bulk Upload Modal */}
            <ExcelUploadModal
                show={showBulkUploadModal}
                onHide={() => setShowBulkUploadModal(false)}
                onSuccess={() => {
                    fetchProducts();
                    // Optionally refresh summary or other data if needed
                }}
            />

            {/* AI Product Suggestions Modal (task 017) */}
            <ProductSuggestionsModal
                show={showAiSuggestionsModal}
                onHide={() => setShowAiSuggestionsModal(false)}
                onAccepted={fetchProducts}
            />

            {/* Generar productos con IA desde la descripción del negocio (task 017 follow-up, 2026-09-01) */}
            <GenerateProductsModal
                show={showGenerateProductsModal}
                onHide={() => setShowGenerateProductsModal(false)}
                onAccepted={fetchProducts}
            />

            {/* Expense Modal */}
            <CreateExpenseModal
                show={showExpenseModal}
                onHide={() => setShowExpenseModal(false)}
                suppliers={suppliers}
                onExpenseCreated={() => {
                    // Optional: refresh or show notification
                }}
            />

            {/* Image Preview Modal (card view — tap image to expand) */}
            <Modal show={!!previewImage} onHide={() => setPreviewImage(null)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title className="fs-6">{previewImage?.name}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center p-0">
                    {previewImage && (
                        <img
                            src={previewImage.url}
                            alt={previewImage.name}
                            style={{ width: '100%', maxHeight: '80vh', objectFit: 'contain' }}
                        />
                    )}
                </Modal.Body>
            </Modal>

            {process.env.NODE_ENV === 'development' && imageUploadDebugError && (
                <Modal show onHide={() => setImageUploadDebugError(null)} size="lg">
                    <Modal.Header closeButton style={{ backgroundColor: 'var(--ev-warning-bg)' }}>
                        <Modal.Title>🐛 Debug: Error al subir imagen</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <table className="table table-sm table-bordered" style={{ fontSize: 13 }}>
                            <tbody>
                                <tr><th style={{ width: 140 }}>Mensaje</th><td>{imageUploadDebugError.message}</td></tr>
                                <tr><th>HTTP Status</th><td>{imageUploadDebugError.status ?? 'Sin respuesta (error de red)'}</td></tr>
                                <tr><th>Status Text</th><td>{imageUploadDebugError.statusText ?? '—'}</td></tr>
                                <tr><th>URL base</th><td style={{ wordBreak: 'break-all' }}>{imageUploadDebugError.baseURL ?? '—'}</td></tr>
                                <tr><th>Endpoint</th><td style={{ wordBreak: 'break-all' }}>{imageUploadDebugError.url ?? '—'}</td></tr>
                                <tr><th>Archivo</th><td>{imageUploadDebugError.fileName}</td></tr>
                                <tr><th>Tipo MIME</th><td>{imageUploadDebugError.fileType}</td></tr>
                                <tr><th>Tamaño</th><td>{(imageUploadDebugError.fileSize / 1024).toFixed(1)} KB</td></tr>
                                <tr>
                                    <th>Respuesta</th>
                                    <td>
                                        <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                            {imageUploadDebugError.data ? JSON.stringify(imageUploadDebugError.data, null, 2) : '—'}
                                        </pre>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setImageUploadDebugError(null)}>Cerrar</Button>
                    </Modal.Footer>
                </Modal>
            )}
        </Container>
    );
};

export default ProductsPage;
